import { Component, createRef } from "react";

import dayjs from "dayjs";
import _ from "lodash";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Tabs, OnOffSwitch, PlainTooltip, TabList, ChangeTooltip } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { NoPrintContainer, WeShare } from "app/components/weshare/weshare";
import { getShouldHideSegments } from "app/components/widget/widget-config/metrics/segments/utils";
import { tickIntervals } from "app/components/widget/widget-types/GraphWidget";
import type { IWidgetModelTypesType } from "app/components/widget/widget-types/Widget";
import { SEGMENTS_METRICS } from "app/components/widget/widget-utilities/segments/constants";
import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { algoChangeDateConfig } from "pro-features/components/Chart/src/configs/plotLines/algoChangeDateConfig";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import xAxisLabelConfigWithLowConfidenceMarker from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelConfigWithLowConfidenceMarker";
import { formatTooltipPointWithConfidence } from "pro-features/components/Chart/src/data/confidenceProcessor";
import { getChangeColor } from "app/pages/website-analysis/TrafficAndEngagement/Metrics/BounceRate";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import { WEB_SOURCE_DISPLAY_NAME } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import Loader from "app/pages/website-analysis/website-content/leading-folders/components/Loader";
import { NoDataSegments } from "app/pages/website-analysis/website-content/leading-folders/components/NoDataSegments";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { FlexColumn, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";
import { US_COUNTRY_ID } from "app/services/countryServiceConst";
import DurationService, { BasicDurations } from "app/services/DurationService";

import Chart from "pro-features/components/Chart/src/Chart";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import { PeriodOverPeriodChart } from "pro-features/components/Chart/src/components/PeriodOverPeriodChart/PeriodOverPeriodChart";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import markerWithDashedConfig from "pro-features/components/Chart/src/configs/series/markerWithDashedLinePerPointChartConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import { granularities } from "pro-features/utils";
import { tooltipPositioner } from "../../../services/HighchartsPositioner";

import SegmentsApiService from "app/services/segments/segmentsApiService";

import {
    CHART_NAME,
    granularityConfigs,
    tabsMeta,
} from "../../website-analysis/website-content/leading-folders/FolderAnalysisDefaults";
import {
    FolderAnalysisContainer,
    MTDLabel,
    SimpleChartContainer,
    StyledScorableTab,
    StyledTabsCarousel,
} from "../../website-analysis/website-content/leading-folders/StyledComponents";
import { SegmentSubtitle } from "pro-features/pages/segments/components/benchmarkOvertime/SegmentsVsGroupLineChart";

import { SwTrack } from "app/services/SwTrack";
import { StyledHeaderTitle } from "app/pages/website-analysis/audience-overlap/StyledComponents";
import { TitleContainerWithBorder } from "app/pages/segments/geography/StyledComponents";
import { MTDTitle } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChartContainer";
import { isPartialDataPoint } from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";
import { getTooltipHeaderElement } from "app/UtilitiesAndConstants/UtilitiesComponents/chartTooltipHeaderWithMTDSupport";
import { AddToDashboardWrapper, DownloadExcelContainer } from "app/pages/segments/analysis/StyledComponents";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { CustomBanner } from "./components/CustomBanner";
import { TRAFFIC_AND_ENGAGEMENT_ENDPOINTS } from "./constants/trafficAndEngagementEndpoints";

import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import type {
    GraphDataEntity,
    GraphDataModifiedEntity,
    IntervalName,
    POPResponseEntity,
} from "app/components/widget/widget-utilities/segments/types";

import type { UniqueUsersResponseEntity } from "./types";

import type { TimeGranularity } from "app/pages/app-intelligence/types";
import { NOT_AVAILABLE } from "app/filters/const";

import { getAlgoChangeDataTimestamp } from "./utils/algoChangeData";

const TooltipWrapper = styled.div`
    white-space: normal;
`;

interface ICustomSegmentsAnalysisProps {
    currentSegment: any;
}
interface ICustomSegmentsAnalysisExcelParams {
    country: string;
    from: string;
    includeSubDomains: boolean;
    isWindow: boolean;
    segmentsIds: string[];
    timeGranularity: string;
    to: string;
    webSource: string;
    keys: string;
    compareFrom?: string;
    compareTo?: string;
    lastUpdated: string;
    latest?: string;
    FileName: string;
}
const weeklyPredicate = (dataPoint) => dataPoint.Value.Confidence > 0 && dataPoint.Value.Confidence < 1;
const monthlyPredicate = (dataPoint) => dataPoint.Value.Confidence > 0 && dataPoint.Value.Confidence < 1;
const UNIQUE_USERS = "UniqueUsers";
const MINIMUM_MONTHLY_MAX_VISITS_DAILY_GRANULARITY_TRESHOLD = 15000;
const dailyGranularityVisiblePredicate = (dataPoint) => {
    return dataPoint.Value?.Value > MINIMUM_MONTHLY_MAX_VISITS_DAILY_GRANULARITY_TRESHOLD;
};

const ENABLED = "enabled";

export default class CustomSegmentsAnalysis extends Component<ICustomSegmentsAnalysisProps, any> {
    private response: any;
    private graphData: any;
    private swNavigator: any;
    private segmentsApiService: SegmentsApiService;
    private tabs: any[];
    private chartRef;

    constructor(props) {
        super(props);
        this.chartRef = createRef();
        this.swNavigator = proNavigator;
        this.segmentsApiService = new SegmentsApiService();
        const { comparedDuration, duration, webSource } = this.swNavigator.getParams();
        const durationRaw = DurationService.getDurationData(duration).raw;
        const monthDiff = durationRaw.to.diff(durationRaw.from, "month");
        const monthDiffThreshold = durationRaw.isCustom ? 0 : 1; //we want to allow in custom duration with 1 month to see Monthly granularity
        this.state = {
            loading: true,
            renderEmpty: false,
            isPOPMode: comparedDuration,
            tab: "Visits",
            timeGranularity:
                monthDiff < monthDiffThreshold && webSource !== devicesTypes.MOBILE
                    ? GranularityNames.WEEKLY
                    : GranularityNames.MONTHLY,
            availableGran: [
                { title: "D", disabled: webSource === devicesTypes.MOBILE },
                { title: "W", disabled: webSource === devicesTypes.MOBILE },
                {
                    title: "M",
                    disabled: duration == BasicDurations.LAST_TWENTY_EIGHT_DAYS,
                },
            ],
            tabSelectedIndex: 0,
            isUniqueUsersTab: false,
            isMTDActive:
                !comparedDuration &&
                !durationRaw.isCustom &&
                webSource !== devicesTypes.MOBILE &&
                duration !== BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            isMTDEnabled:
                !(comparedDuration || duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS || durationRaw.isCustom) &&
                webSource !== devicesTypes.MOBILE,
        };
    }

    public async componentDidMount() {
        try {
            this.response = await this.getResponseData();
        } catch {
            this.setState({
                loading: false,
            });
        }
        this.setResponse();
    }

    // this component render twice when POP mode changed, first render happening when isPOPMode props changed
    // and its run before ****** render all the component and fetch the new appropriate data
    public shouldComponentUpdate(nextProps, nextState) {
        if (nextState.isPOPMode !== this.state.isPOPMode) {
            return false;
        }
        return true;
    }

    public componentDidUpdate(
        prevProps: Readonly<ICustomSegmentsAnalysisProps>,
        prevState: Readonly<any>,
        snapshot?: any,
    ): void {
        const { duration, comparedDuration, webSource } = this.swNavigator.getParams();
        const { currentSegment } = this.props;
        const { tab, timeGranularity, isPOPMode, availableGran } = this.state;
        const isUniqueTab = tab === UNIQUE_USERS;
        const isPreviousYear = comparedDuration === "12m";
        const currentGranularityIndex = granularities.indexOf(timeGranularity);

        // if we are on unique users tab then we don't need to update the granularity
        if (prevState.isMTDActive !== this.state.isMTDActive) {
            this.refreshResponseData();
        }
        if (isUniqueTab) {
            return;
        } else {
            const monthlyGraphVisits = isPOPMode
                ? _.get(
                      this.response,
                      `["Interval"][${currentSegment?.id}][${tabsMeta.Visits.id}][${granularities[2]}][Graph]`,
                      0,
                  )
                : _.get(this.response, `[${currentSegment?.id}][${tabsMeta.Visits.id}][${granularities[2]}][Graph]`, 0);
            const monthlyVisitsCrossThreshold = _.filter(monthlyGraphVisits, dailyGranularityVisiblePredicate);
            const weeklyTabData = _.get(
                this.response,
                `[${currentSegment?.id}][${this.state.tab}][${granularities[1]}]`,
                { Confidence: 0 },
            );

            const weeklyConfindenceGroup = _.filter(weeklyTabData.Graph, weeklyPredicate);
            const showDailyOnPop = isPreviousYear || duration === "1m"; // daily disabled on pop only on previous period and duration larger then a month.
            const weeklyDisabled = weeklyConfindenceGroup.length === 0 || webSource === devicesTypes.MOBILE;
            const dailyDisabled =
                monthlyVisitsCrossThreshold.length === 0 ||
                webSource === devicesTypes.MOBILE ||
                (isPOPMode && !showDailyOnPop);

            if (weeklyDisabled !== availableGran[1].disabled || dailyDisabled !== availableGran[0].disabled) {
                this.setState({
                    isPOPMode: comparedDuration,
                    availableGran: [
                        { title: "D", disabled: dailyDisabled },
                        { title: "W", disabled: weeklyDisabled },
                        { title: "M", disabled: duration == BasicDurations.LAST_TWENTY_EIGHT_DAYS },
                    ],
                });
            }

            // check if current granularity is enabled, if not then we will search and switch to one that is not disbaled:
            if (availableGran[currentGranularityIndex].disabled) {
                this.switchToAvailableGranularity();
            }
        }
    }

    // method that check if granularity is enabled, if not then switch to one that is:
    public switchToAvailableGranularity = () => {
        const { availableGran, tab } = this.state;
        for (let i = 0; i < availableGran.length; i++) {
            if (!availableGran[i].disabled) {
                this.setState({
                    timeGranularity: granularities[i],
                });
                this.setCurrentData(tab, granularities[i]);
                break;
            }
        }
    };

    public onTabSelected = (index: number, enabled = true) => {
        const { tabSelectedIndex } = this.state;
        this.setState({
            renderEmpty: false,
        });

        if (!enabled || tabSelectedIndex === index) {
            return false;
        }
        const { timeGranularity } = this.state;
        const uniqueUsersTabIndex = Object.values(tabsMeta).findIndex((tab) => tab.id === tabsMeta.UniqueUsers.id);
        const isUniqueUsersTab = index === uniqueUsersTabIndex;

        if (isUniqueUsersTab) {
            this.setState({
                availableGran: [
                    { title: "D", disabled: true },
                    { title: "W", disabled: true },
                    { title: "M", disabled: false },
                ],
            });
            if (timeGranularity !== granularities[2]) {
                this.setState({
                    timeGranularity: granularities[2],
                });
            }
            this.setCurrentData(this.tabs[index].id, granularities[2]);
        } else {
            this.setCurrentData(this.tabs[index].id, this.state.timeGranularity);
        }

        SwTrack.all.trackEvent("Metric Button", "click", `${CHART_NAME}/${this.tabs[index].id}`);

        this.setState({
            tab: this.tabs[index].id,
            tabSelectedIndex: index,
        });
        return true;
    };

    public isTabEnabled = (tabValue): boolean => {
        return tabValue !== NOT_AVAILABLE;
    };

    public fetchUniqueUsersData = () => {
        const { currentSegment } = this.props;
        const segmentApiService = new SegmentsApiService();
        const params = this.swNavigator.getParams();
        const durationApi = DurationService.getDurationData(params.duration, params.comparedDuration);
        const timeGran = durationApi?.forAPI?.isWindow ? granularities[0] : granularities[2];

        return segmentApiService.getUniqueUsersData({
            country: params.country,
            from: durationApi.forAPI.from,
            to: durationApi.forAPI.to,
            compareFrom: durationApi.forAPI?.compareFrom,
            compareTo: durationApi.forAPI?.compareTo,
            includeSubDomains: true,
            isWindow: durationApi?.forAPI?.isWindow,
            keys: null,
            segmentsIds: [currentSegment.id],
            timeGranularity: timeGran,
            webSource: params.webSource,
            lastUpdated: currentSegment.lastUpdated,
        });
    };

    public fetchSegmentData = () => {
        const { currentSegment } = this.props;
        if (!currentSegment) {
            return;
        }
        const segmentApiService = new SegmentsApiService();
        const params = this.swNavigator.getParams();
        const durationApi = DurationService.getDurationData(params.duration, params.comparedDuration);
        const timeGran = durationApi?.forAPI?.isWindow ? granularities[0] : granularities[2];
        return segmentApiService.getCustomSegmentEngagementData({
            country: params.country,
            from: durationApi.forAPI.from,
            to: durationApi.forAPI.to,
            compareFrom: durationApi?.forAPI?.compareFrom,
            compareTo: durationApi?.forAPI?.compareTo,
            includeSubDomains: true,
            isWindow: durationApi?.forAPI?.isWindow,
            keys: null,
            segmentsIds: [currentSegment.id],
            timeGranularity: timeGran,
            webSource: params.webSource,
            lastUpdated: currentSegment.lastUpdated,
            latest:
                !this.state.isPOPMode &&
                !durationApi.raw.isCustom &&
                params.webSource !== devicesTypes.MOBILE &&
                this.state.isMTDActive
                    ? "l"
                    : undefined,
        });
    };

    public async refreshResponseData() {
        this.setState({
            loading: true,
        });
        try {
            this.response = await this.getResponseData();
        } catch {
            this.setState({
                loading: false,
            });
        }
        this.setResponse();
    }

    public async getResponseData() {
        const { duration, webSource } = this.swNavigator.getParams();
        const { isPOPMode } = this.state;
        const isNotDesktopSource = webSource !== devicesTypes.DESKTOP;

        if (
            (this.state.isPOPMode && isNotDesktopSource) ||
            duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS ||
            isNotDesktopSource
        ) {
            return await this.fetchSegmentData();
        }

        // is we are not on PopMode then we will fetch unique users tab as well and change our data for stracture for supporting it:
        return await Promise.all([this.fetchSegmentData(), this.fetchUniqueUsersData()]).then((values) => {
            // @ts-ignore FIXME: strict migration - Argument of type '[unknown, unknown]' is not assignable to parameter of type '[POPResponseEntity, UniqueUsersResponseEntity]'
            return isPOPMode ? this.mergeResponsesPoP(values) : this.transformUniqueAnEngagementResponse(values);
        });
    }

    // This method is copy of transformUniqueAnEngagementResponse only applied on PoP
    public mergeResponsesPoP = ([segmentMetrics, uniqueUsersMetric]: [
        POPResponseEntity,
        UniqueUsersResponseEntity,
    ]) => {
        const { currentSegment } = this.props;
        const { timeGranularity } = this.state;

        const newResponse = { ...segmentMetrics };
        const segmentId = currentSegment?.id;

        const getGraphFromResponse = (
            response: POPResponseEntity | UniqueUsersResponseEntity,
            interval: IntervalName,
            id: number | string,
            metric: string,
            granularity: TimeGranularity,
        ) => _.get(response, `${interval}.${id}.${metric}.${granularity}.Graph`);

        const modifyGraphData = (acc: Array<GraphDataModifiedEntity>, val: GraphDataEntity, index: number) => {
            acc.push({
                ...val,
                Value: {
                    Value: val?.Value,
                    Confidence: _.get(visitsGraph, `[${index}].Value.Confidence`),
                },
            });

            return acc;
        };

        const visitsGraph = getGraphFromResponse(
            segmentMetrics,
            "ComparedInterval",
            segmentId,
            "Visits",
            timeGranularity,
        );

        const ComparedIntervalGraph: GraphDataEntity[] = getGraphFromResponse(
            uniqueUsersMetric,
            "ComparedInterval",
            segmentId,
            "UniqueUsers",
            timeGranularity,
        );

        const IntervalGraph: GraphDataEntity[] = getGraphFromResponse(
            uniqueUsersMetric,
            "Interval",
            segmentId,
            "UniqueUsers",
            timeGranularity,
        );

        const ComparedInterval = ComparedIntervalGraph.reduce(modifyGraphData, []);

        const Interval = IntervalGraph.reduce(modifyGraphData, []);

        // TODO: Fix TS errors here, who ever is responsible for this file
        // @ts-ignore
        newResponse.ComparedInterval[segmentId].UniqueUsers = _.get(
            uniqueUsersMetric,
            `ComparedInterval[${segmentId}].UniqueUsers`,
        );

        // TODO: Fix TS errors here, who ever is responsible for this file
        // @ts-ignore
        newResponse.Interval[segmentId].UniqueUsers = _.get(uniqueUsersMetric, `Interval[${segmentId}].UniqueUsers`);

        //Confidence is not provided currently for Unique users metric so we add it from visits metric
        const getConfidence = (
            response: POPResponseEntity,
            interval: IntervalName,
            id: string | number,
            metric: string,
            granularity: TimeGranularity,
        ) => _.get(response, `${interval}.${id}.${metric}.${granularity}.Confidence`);

        const ConfidenceInterval = getConfidence(newResponse, "Interval", segmentId, "Visits", timeGranularity);

        const ConfidenceComparedInterval = getConfidence(
            newResponse,
            "ComparedInterval",
            segmentId,
            "Visits",
            timeGranularity,
        );

        const setUniqueUsers = (
            interval: IntervalName,
            id: string | number,
            metric: string,
            granularity: TimeGranularity,
            value: Record<string, unknown> | GraphDataModifiedEntity[],
            isGraph?: boolean,
        ) =>
            value &&
            _.merge(
                newResponse,
                _.set({}, `${interval}.${id}.${metric}.${granularity}${isGraph ? ".Graph" : ""}`, value),
            );

        setUniqueUsers("Interval", segmentId, "UniqueUsers", GranularityNames.MONTHLY, {
            Confidence: ConfidenceInterval,
        });

        setUniqueUsers("ComparedInterval", segmentId, "UniqueUsers", GranularityNames.MONTHLY, {
            Confidence: ConfidenceComparedInterval,
        });

        setUniqueUsers("Interval", segmentId, "UniqueUsers", GranularityNames.MONTHLY, Interval, true);

        setUniqueUsers("ComparedInterval", segmentId, "UniqueUsers", GranularityNames.MONTHLY, ComparedInterval, true);

        return newResponse;
    };

    public transformUniqueAnEngagementResponse = (values) => {
        const { currentSegment } = this.props;
        const newResponse = values[0];
        const uniqueUsersGraph = values[1][currentSegment?.id]?.UniqueUsers?.Monthly?.Graph;
        const visitsGraph = values[0][currentSegment?.id]?.Visits?.Monthly?.Graph;

        uniqueUsersGraph.forEach((val, index) => {
            val.Value = {
                Value: val?.Value,
                Confidence: visitsGraph[index]?.Value?.Confidence,
            };
        });

        // adding unique users into the segmentData result
        Object.assign(newResponse[currentSegment?.id], values[1][currentSegment?.id]);
        // adding confidence into unique users:
        const Confidence = newResponse[currentSegment?.id]?.Visits?.Monthly?.Confidence;
        Confidence &&
            Object.assign(newResponse[currentSegment?.id]?.UniqueUsers?.Monthly, {
                Confidence: Confidence,
            });

        // adding the currect graph stracture for unique users:
        uniqueUsersGraph &&
            Object.assign(newResponse[currentSegment?.id]?.UniqueUsers?.Monthly.Graph, uniqueUsersGraph);

        return newResponse;
    };

    public isObjectEmpty = (obj) => {
        return Object.keys(obj).length === 0;
    };

    public renderExcelComponent = () => {
        const { currentSegment } = this.props;
        const params = this.swNavigator.getParams();
        const durationObj = DurationService.getDurationData(params.duration, params.comparedDuration);
        const { timeGranularity } = this.state;
        return (
            <DownloadExcelContainer>
                <DownloadButtonMenu
                    Excel={true}
                    downloadUrl={this.getExcelUrl(currentSegment, params, durationObj, timeGranularity)}
                    excelLocked={false}
                />
            </DownloadExcelContainer>
        );
    };
    public toggleMTD = () => {
        this.setState({
            ...this.state,
            isMTDActive: !this.state.isMTDActive,
        });
    };

    public getLastWindowDate = () => {
        return swSettings.current.lastSupportedDailyDate;
    };

    public getMTDTooltipContent = () => {
        return translate("wa.mmx.channel_analysis.mtd.tooltip.last_available_date", {
            d: this.getLastWindowDate()?.format("MMM DD"),
        });
    };

    public getSubtitleFilters = () => {
        const filters = this.swNavigator.getApiParams();
        return [
            {
                filter: "date",
                value: {
                    from: filters.from,
                    to: filters.to,
                },
            },
            {
                filter: "country",
                countryCode: filters?.country,
                value: CountryService.getCountryById(filters?.country).text,
            },
            {
                filter: "webSource",
                value: filters.webSource,
            },
        ];
    };

    public render() {
        const { currentSegment } = this.props;
        const mtdTooltipText = this.getMTDTooltipContent();
        const { tabSelectedIndex, isMTDActive, isMTDEnabled, timeGranularity } = this.state;
        const height = 630;
        const params = this.swNavigator.getParams();
        const redirectUrl = this.swNavigator.getStateUrl(this.swNavigator.current().name, params);
        const { webSource } = params;
        const durationObj = DurationService.getDurationData(params.duration, params.comparedDuration);

        const GranularityComponent = <RightFlexRow>{this.getSwitcherComponent()}</RightFlexRow>;
        if (!currentSegment) {
            return null;
        }

        if (this.state.loading) {
            return (
                <Loader
                    height={height}
                    timeGranularity={this.state.timeGranularity}
                    hideDD
                />
            );
        }

        if (!this.response) {
            return (
                <FolderAnalysisContainer>
                    <TableNoData
                        icon="no-data"
                        messageTitle={translate("global.nodata.notavilable")}
                    />
                </FolderAnalysisContainer>
            );
        }

        return (
            <FolderAnalysisContainer
                className="folder-analysis-container"
                height={height}
                ref={this.chartRef}
            >
                <TitleContainerWithBorder>
                    <FlexColumn>
                        <StyledHeaderTitle>
                            <BoxTitle>{translate("segment.analysis.traffic.and.engagement.title")}</BoxTitle>
                        </StyledHeaderTitle>
                        <StyledBoxSubtitle>
                            <BoxSubtitle filters={this.getSubtitleFilters()} />
                        </StyledBoxSubtitle>
                    </FlexColumn>
                    <NoPrintContainer>
                        <RightFlexRow>
                            {isMTDEnabled && this.tabs[tabSelectedIndex].id !== tabsMeta.UniqueUsers.id ? (
                                <PlainTooltip
                                    enabled={!!mtdTooltipText}
                                    tooltipContent={mtdTooltipText}
                                >
                                    <MTDTitle onClick={this.toggleMTD}>
                                        <OnOffSwitch
                                            isSelected={isMTDActive}
                                            onClick={_.noop}
                                        />
                                        <StyledBoxSubtitle>
                                            <MTDLabel>{translate("segment.analysis.mtd.toggle_label")}</MTDLabel>
                                        </StyledBoxSubtitle>
                                    </MTDTitle>
                                </PlainTooltip>
                            ) : null}
                            {this.renderExcelComponent()}

                            <WeShare
                                chartRef={this.chartRef.current}
                                title={`${this.props.currentSegment.domain} ${translate(
                                    "segment.analysis.traffic.and.engagement.title",
                                )}`}
                                description={`${this.tabs[tabSelectedIndex].value} ${this.tabs[tabSelectedIndex].title}`}
                                redirectUrl={`https://test.groupbuyclub.net/${redirectUrl}`}
                            />
                        </RightFlexRow>
                    </NoPrintContainer>
                </TitleContainerWithBorder>
                <NoPrintContainer>
                    <Tabs selectedIndex={tabSelectedIndex}>
                        <TabList>
                            <StyledTabsCarousel
                                offset={0}
                                margin={0}
                            >
                                {this.tabs
                                    ? this.tabs.map((tab: any, index) => {
                                          const tabEnabled = tab.hasOwnProperty(ENABLED)
                                              ? tab[ENABLED]
                                              : this.isTabEnabled(tab.value);

                                          return (
                                              <span
                                                  className="ScorableTabWrapper"
                                                  onClick={() => {
                                                      this.onTabSelected(index, tabEnabled);
                                                  }}
                                                  key={tab.id}
                                              >
                                                  <StyledScorableTab
                                                      key={tab.id}
                                                      roughEstimation={tab.roughEstimation}
                                                      selected={tab.active}
                                                      metric={tab.title}
                                                      metricIcon={tab.icon}
                                                      invertChangeColors={tab.invertColors}
                                                      value={tab.value}
                                                      valueTooltip={tab.infoIcon}
                                                      changeTooltip={tab.growthTooltip}
                                                      valueChange={tab.changeValue}
                                                      valueChangeFormat={"0.00%"}
                                                      hideBorder={true}
                                                      className={"scorable-tab"}
                                                      enabled={tabEnabled}
                                                  />
                                              </span>
                                          );
                                      })
                                    : null}
                            </StyledTabsCarousel>
                        </TabList>
                    </Tabs>
                </NoPrintContainer>
                {this.state.renderEmpty ? (
                    <NoDataSegments
                        messageBottomHeader={translate("segments.nodata.notavilable")}
                        messageBottomText={translate("segments.nodata.notavilable.subtitle")}
                    />
                ) : (
                    <SimpleChartContainer>
                        {this.state.isPOPMode ? (
                            <PeriodOverPeriodChart
                                type="line"
                                legendDurations={
                                    typeof durationObj.forWidget === "string"
                                        ? [durationObj.forWidget]
                                        : durationObj.forWidget.reverse()
                                }
                                showLegend={true}
                                rightSectionComponent={GranularityComponent}
                                options={{ height: 340 }}
                                data={{ [WEB_SOURCE_DISPLAY_NAME[webSource]]: this.graphData }}
                                metric={this.tabs[tabSelectedIndex].id}
                                yAxisFormatter={this.getPOPyAxisFormatter()}
                                timeGranularity={timeGranularity}
                            />
                        ) : (
                            <>
                                <NoPrintContainer>{GranularityComponent}</NoPrintContainer>
                                <Chart
                                    type="line"
                                    config={this.getChartConfig("line")}
                                    data={this.graphData}
                                    isFinalConfig={true}
                                />
                            </>
                        )}
                    </SimpleChartContainer>
                )}
            </FolderAnalysisContainer>
        );
    }

    public getExcelUrl = ({ id, lastUpdated, segmentName, domain }: any, params, durationApi, timeGran) => {
        const requestParams = (params: any): string => {
            return _.toPairs(params)
                .map(
                    ([key, value]) =>
                        `${encodeURIComponent(key)}=${value ? encodeURIComponent(value.toString()) : value}`,
                )
                .join("&");
        };

        const excelFileName = `SegmentAnalysis - ${segmentName} - (${params.country}) - (${
            durationApi.forAPI.from
        }) - (${durationApi.forAPI.to})
            ${
                this.state.isPOPMode
                    ? ` - AND (${durationApi.forAPI?.compareFrom}) - (${durationApi.forAPI?.compareTo})`
                    : ""
            }`;
        timeGran = timeGran ?? (durationApi?.forAPI?.isWindow ? granularities[0] : granularities[2]);
        const requestParamsObj: ICustomSegmentsAnalysisExcelParams = {
            country: params.country,
            from: durationApi.forAPI?.from,
            to: durationApi.forAPI?.to,
            compareFrom: durationApi.forAPI?.compareFrom,
            compareTo: durationApi.forAPI?.compareTo,
            includeSubDomains: true,
            isWindow: durationApi?.forAPI?.isWindow,
            keys: null,
            segmentsIds: [id],
            timeGranularity: timeGran,
            webSource: params.webSource || devicesTypes.DESKTOP,
            lastUpdated,
            FileName: excelFileName,
        };
        if (
            !this.state.isPOPMode &&
            !durationApi?.raw?.isCustom &&
            params.webSource !== devicesTypes.MOBILE &&
            this.state.isMTDActive
        ) {
            requestParamsObj.latest =
                params.duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS ? BasicDurations.LAST_TWENTY_EIGHT_DAYS : "l";
        }
        const { webSource = devicesTypes.DESKTOP, ...paramsWithoutWebSource } = requestParamsObj;
        const endpoint = TRAFFIC_AND_ENGAGEMENT_ENDPOINTS[webSource].excel;
        return `/${endpoint}?${requestParams(paramsWithoutWebSource)}`;
    };

    public getSwitcherComponent = () => {
        if (!Array.isArray(this.state.availableGran) || this.state.availableGran.length === 0) {
            return null;
        }
        const shouldHideSegments = getShouldHideSegments();

        return (
            <>
                <SwitcherGranularityContainer
                    itemList={this.state.availableGran}
                    selectedIndex={granularities.indexOf(this.state.timeGranularity)}
                    onItemClick={this.onTimeGranularityClick}
                    className="gran-switch"
                />
                {!shouldHideSegments ? this.getAddToDashboardButton() : null}
            </>
        );
    };

    public getAddToDashboardButton = () => {
        const { currentSegment } = this.props;
        const { timeGranularity } = this.state;
        const { webSource } = this.swNavigator.getParams();

        return (
            <AddToDashboardWrapper>
                <AddToDashboard
                    metric={SEGMENTS_METRICS?.[this.state.tab]?.name}
                    type={SEGMENTS_METRICS?.[this.state.tab]?.type as IWidgetModelTypesType}
                    webSource={webSource}
                    filters={{
                        timeGranularity: timeGranularity,
                        segmentId: currentSegment?.id,
                    }}
                    modelType="fromSegment"
                    overrideAddToDashboardParams={{
                        key: [
                            {
                                displayName: currentSegment?.domain,
                                name: currentSegment?.segmentName,
                                image: currentSegment?.favicon,
                                id: currentSegment?.id,
                            },
                        ],
                    }}
                />
            </AddToDashboardWrapper>
        );
    };

    public setResponse = () => {
        if (this.response) {
            this.setCurrentData(this.state.tab, this.state.timeGranularity);
            this.tabs = this.getTabs();
            this.setState({
                loading: false,
            });
        }
    };

    public confideceValidator = (
        confidence: number,
        timeGranularity: GranularityNames,
        graphData: GraphDataEntity[],
    ): boolean => {
        const confindenceGroup = _.filter(
            graphData,
            timeGranularity === GranularityNames.WEEKLY ? weeklyPredicate : monthlyPredicate,
        );

        return confidence === 0 || confidence >= 1 || confindenceGroup.length === 0;
    };

    public setCurrentData = (tab, timeGranularity) => {
        // this.graphData & this.lastPoint are changed only in here
        const { currentSegment } = this.props;
        const { duration, comparedDuration } = this.swNavigator.getParams();
        const durationObj = DurationService.getDurationData(duration, comparedDuration);

        const tabData = this.state.isPOPMode
            ? _.get(this.response, `["Interval"][${currentSegment?.id}][${tab}][${timeGranularity}]`, { Confidence: 0 })
            : _.get(this.response, `[${currentSegment?.id}][${tab}][${timeGranularity}]`, {
                  Confidence: 0,
              });

        if (this.confideceValidator(tabData.Confidence, timeGranularity, tabData.Graph)) {
            this.setState({
                renderEmpty: true,
            });
            return;
        } else {
            this.setState({
                renderEmpty: false,
            });
        }
        if (this.state.isPOPMode) {
            const tabDataCompared = _.get(
                this.response,
                `["ComparedInterval"][${currentSegment?.id}][${tab}][${timeGranularity}]`,
                { Confidence: 0 },
            );
            const data = tabData?.Graph.map((item, idx) => {
                return {
                    Values: [
                        {
                            Key: item.Key,
                            Value: item.Value.Value,
                            Confidence: item.Value.Confidence,
                        },
                        {
                            Key: tabDataCompared?.Graph[idx]?.Key,
                            Value: tabDataCompared?.Graph[idx]?.Value.Value,
                            Confidence: tabDataCompared?.Graph[idx]?.Value.Confidence,
                        },
                    ],
                    Change: [this.calcChange(item.Value.Value, tabDataCompared?.Graph[idx]?.Value.Value)],
                };
            });
            this.graphData = data;
        } else {
            const data = tabData.Graph.map((item, index, arr) => {
                if (item === undefined) {
                    return [];
                }
                return [
                    new Date(item.Key).getTime(),
                    item.Value.Value,
                    {
                        partial:
                            (item.Value.Confidence >= 0.3 && item.Value.Confidence < 1) ||
                            isPartialDataPoint(
                                index,
                                arr,
                                item,
                                timeGranularity,
                                durationObj.forAPI.to.replace(/\|/g, "/"),
                            ),
                        hallowMarker: item.Value.Confidence >= 0.3 && item.Value.Confidence < 1,
                        confidenceLevel: item.Value.Confidence,
                    },
                ];
            });
            this.graphData = [{ data }];
        }
    };

    public getTabs = () => {
        return Object.keys(tabsMeta).map((tab) => {
            return { ...this.getTabProps(tab), id: tab };
        });
    };

    public calcChange = (currentMonth, comparedMonth) => {
        return (currentMonth - comparedMonth) / comparedMonth;
    };

    public getTabProps = (tab) => {
        const { response } = this;
        const { currentSegment } = this.props;
        const { isPOPMode } = this.state;
        const timeGranularity = tab === tabsMeta.UniqueUsers.id ? GranularityNames.MONTHLY : this.state.timeGranularity;
        const changeValue = this.getTabChange(tab);
        const params = this.swNavigator.getParams();
        const { webSource, country } = params;
        const tabMeta = tabsMeta[tab];
        const isEnabledDueToUS = parseInt(country) === US_COUNTRY_ID && webSource !== devicesTypes.TOTAL;
        const enabled = isEnabledDueToUS ? true : tabMeta.supportedWebSources.includes(webSource);
        const comingSoon = translate("common.coming.soon");
        const tabData = isPOPMode
            ? _.get(response, `["Interval"][${currentSegment?.id}][${tab}][${timeGranularity}]`)
            : _.get(response, `[${currentSegment?.id}][${tab}][${timeGranularity}]`);

        const confidence = tabData?.Confidence || 0;

        const isTabDataNotAvailable = this.confideceValidator(confidence, timeGranularity, tabData?.Graph);

        const getTabValue = (): string => {
            if (!enabled) {
                return comingSoon;
            } else {
                if (isTabDataNotAvailable) {
                    return NOT_AVAILABLE;
                }
            }
            return this.getTabAverage(tab);
        };

        return {
            active: this.state.tab === tab,
            roughEstimation: response ? confidence > 0.3 : false,
            icon: tabsMeta[tab].icon,
            title:
                tab === "Visits"
                    ? translate(`folderanalysis.tabs.avg.monthly`) + translate(`${tabsMeta[tab].title}`)
                    : translate(`${tabsMeta[tab].title}`),
            value: getTabValue(),
            [ENABLED]: enabled,
            changeValue: enabled && !isTabDataNotAvailable ? changeValue : null,
            invertColors: tabsMeta[tab].invertChange,
            showTabChange: false,
            infoIcon: false,
            growthTooltip: this.getTabGrowthTooltipText(tab),
        };
    };

    public getTabGrowthTooltipText = (tab) => {
        const { currentSegment } = this.props;
        const { timeGranularity, isPOPMode, isMTDActive } = this.state;
        const graphData = isPOPMode
            ? _.get(this.response?.Interval, `[${currentSegment?.id}]["${tab}"]["${timeGranularity}"].Graph`)
            : _.get(this.response, `[${currentSegment?.id}]["${tab}"]["${timeGranularity}"].Graph`);
        if (graphData === undefined || graphData.length === 0) {
            return "";
        }

        return `${dayjs(_.get(graphData, "[0].Key")).format("MMM YYYY")} vs. ${dayjs(
            _.get(
                graphData,
                `[${isMTDActive && tab !== tabsMeta.UniqueUsers.id ? graphData.length - 2 : graphData.length - 1}].Key`,
            ),
        ).format("MMM YYYY")}`;
    };

    public getTabAverage = (tab) => {
        const { currentSegment } = this.props;
        const { timeGranularity, isPOPMode } = this.state;
        const filter = tabsMeta[tab].filter;
        const customTimeGranularity = GranularityNames.MONTHLY;
        const isUniqueTab = tab === UNIQUE_USERS;
        const PopGranularityToUse = isUniqueTab ? customTimeGranularity : timeGranularity;

        const value = isPOPMode
            ? _.get(this.response, `["Interval"][${currentSegment?.id}]["${tab}"]["${PopGranularityToUse}"].Average`)
            : _.get(this.response, `[${currentSegment?.id}]["${tab}"][${customTimeGranularity}].Average`);
        if (value === 0 || value == null) {
            return NOT_AVAILABLE;
        }
        return filter[0]()(value, filter[1]);
    };

    public getTabChange = (tab) => {
        const { currentSegment } = this.props;
        const { isPOPMode, isMTDActive } = this.state;

        const { duration } = this.swNavigator.getParams();

        if (duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS || duration === "1m") {
            return undefined;
        }

        const graphData = isPOPMode
            ? _.get(this.response, `["Interval"][${currentSegment?.id}]["${tab}"][${GranularityNames.MONTHLY}].Graph`)
            : _.get(this.response, `[${currentSegment?.id}]["${tab}"][${GranularityNames.MONTHLY}].Graph`);
        if (!graphData || !Array.isArray(graphData)) {
            return;
        }
        const transformedData = graphData.map((item) => {
            if (item === undefined) {
                return [];
            }
            return [
                new Date(item.Key).getTime(),
                item.Value.Value,
                {
                    partial: item.Value.Confidence >= 0.3 && item.Value.Confidence < 1,
                    hallowMarker: item.Value.Confidence >= 0.3 && item.Value.Confidence < 1,
                    confidenceLevel: item.Value.Confidence,
                },
            ];
        });
        if (transformedData && transformedData.length > 1 && transformedData[0][1]) {
            return (
                transformedData[
                    isMTDActive && tab !== tabsMeta.UniqueUsers.id
                        ? transformedData.length - 2
                        : transformedData.length - 1
                ][1] /
                    transformedData[0][1] -
                1
            );
        }
        return undefined;
    };

    public onTimeGranularityClick = (index) => {
        const timeGranularity = granularities[index];
        if (this.state.timeGranularity !== timeGranularity) {
            this.setCurrentData(this.state.tab, timeGranularity);
            this.setState({
                timeGranularity,
            });
            SwTrack.all.trackEvent("Time Frame Button", "switch", `Over Time Graph/${CHART_NAME}/${timeGranularity}`);
        }
    };

    public getChangeTooltip = (
        selectedTab: (typeof tabsMeta)[keyof typeof tabsMeta],
        domain,
        granularity = GranularityNames.MONTHLY,
        toDateMoment = undefined,
        algoChangeDate = undefined,
    ) => {
        const {
            chartTitle: metric,
            filter,
            invertChange: invertTooltipColor = false,
            displayCustomAnnotation,
        } = selectedTab;
        return {
            tooltip: {
                positioner: tooltipPositioner,
                shared: true,
                crosshairs: true,
                outside: false,
                useHTML: true,
                followPointer: false,
                formatter() {
                    const getChange = (previousDataPoint, change) =>
                        previousDataPoint
                            ? change !== 0 && percentageSignFilter()(change, 2)
                            : translate("common.tooltip.change.new");

                    const changeTooltipProp = () => {
                        return this.points.map((row) => {
                            const pointIndex = row.point.index;
                            const previousDataPoint =
                                pointIndex === 0 ? row.series?.data[pointIndex].y : row.series?.data[pointIndex - 1].y;
                            const change = row.y / previousDataPoint - 1;
                            return {
                                displayName: domain,
                                value: formatTooltipPointWithConfidence(
                                    row.y,
                                    _.get(row, "[point][confidence]", undefined),
                                    filter,
                                ),
                                color: row.color,
                                subtitle: <SegmentSubtitle>{row.series.name}</SegmentSubtitle>,
                                change: previousDataPoint !== 0 && getChange(previousDataPoint, change),
                            };
                        });
                    };
                    const changeTooltipProps = {
                        rowHeight: 34,
                        header: getTooltipHeaderElement(
                            granularity,
                            this.x,
                            swSettings.current.lastSupportedDailyDate.isAfter(toDateMoment)
                                ? swSettings.current.lastSupportedDailyDate
                                : toDateMoment,
                        ),
                        tableHeaders: [
                            { position: 1, displayName: `${granularity} ${translate(metric)}` },
                            { position: 0, displayName: "Segment" },
                            { position: 2, displayName: "Change" },
                        ],
                        tableRows: changeTooltipProp(),
                        showChangeColumn: true,
                    };
                    if (invertTooltipColor) {
                        changeTooltipProps["getChangeColor"] = getChangeColor;
                    }
                    const shouldDisplayCustomTooltipBanner = algoChangeDate === this.x && displayCustomAnnotation;
                    return ReactDOMServer.renderToString(
                        <TooltipWrapper>
                            <ChangeTooltip {...changeTooltipProps} />
                            {shouldDisplayCustomTooltipBanner ? <CustomBanner /> : null}
                        </TooltipWrapper>,
                    );
                },
            },
        };
    };

    public getChartConfig = (type) => {
        const { tab, timeGranularity } = this.state;
        const params = this.swNavigator.getParams();
        const durationApi = DurationService.getDurationData(params.duration, params.comparedDuration);
        const { graphData } = this;
        const format = this.state.availableGran[2].disabled ? "DD MMM" : "MMM";
        const currentGranularity = granularityConfigs[this.state.timeGranularity];
        const selectedTab = tabsMeta[tab];
        const { filter, displayCustomAnnotation } = selectedTab;
        const yAxisFilter = tabsMeta[this.state.tab].yAxisFilter;
        const yAxisFormatter = ({ value }) =>
            yAxisFilter ? yAxisFilter[0]()(value, filter[1]) : filter[0]()(value, filter[1]);
        const xAxisFormatter = ({ value }) => dayjs.utc(value).utc().format(format);
        const algoChangeDate = getAlgoChangeDataTimestamp();
        const isTurboThreshold = timeGranularity.name === "Daily";
        const config = combineConfigs(
            {
                isWindow: durationApi?.forAPI?.isWindow,
                windowToMoment: durationApi?.raw?.to,
                type,
                granularity: timeGranularity,
                yAxisFormatter,
                xAxisFormatter,
                data: graphData[0].data,
                filter,
                showSeriesBulletColor: false,
            },
            [
                algoChangeDate && displayCustomAnnotation
                    ? algoChangeDateConfig(algoChangeDate, {
                          showTooltip: true,
                          description: translate("custom.segment.analysis.tooltip.unique.users.plotline"),
                          overwriteDateRange: true,
                          showDateTitle: false,
                      })
                    : {},
                currentGranularity,
                noLegendConfig,
                tooltipContainerConfig,
                this.getChangeTooltip(
                    selectedTab,
                    this.props.currentSegment.domain,
                    timeGranularity,
                    durationApi?.raw?.to,
                    algoChangeDate,
                ),
                yAxisLabelsConfig,
                xAxisLabelConfigWithLowConfidenceMarker,
                markerWithDashedConfig,
                {
                    chart: {
                        height: 340,
                        type,
                        spacingTop: 10,
                        plotBackgroundColor: "transparent",
                        events: {},
                    },
                    plotOptions: {
                        area: {
                            fillColor: "rgba(220,220,249,0.1)",
                            lineWidth: 1,
                            lineColor: colorsPalettes.blue[400],
                            connectNulls: false,
                        },
                        line: {
                            fillColor: "rgba(220,220,249,0.1)",
                            lineWidth: 2,
                            lineColor: colorsPalettes.blue[400],
                            connectNulls: false,
                        },
                        series: {
                            name: this.props.currentSegment?.segmentName,
                            connectNulls: false,
                            stickyTracking: true,
                            color: colorsPalettes.blue[400],
                            turboThreshold: isTurboThreshold ? 1000 : null,
                        },
                    },
                    yAxis: {
                        gridLineWidth: 0.5,
                        showFirstLabel: true,
                        showLastLabel: true,
                        reversed: false,
                        gridZIndex: 2,
                        reversedStacks: true,
                        tickPixelInterval: 50,
                        labels: {
                            style: {
                                textTransform: "uppercase",
                                fontSize: "11px",
                                color: "#919191",
                            },
                        },
                    },
                    xAxis: {
                        gridLineWidth: 0,
                        gridLineDashStyle: "dash",
                        tickLength: 5,
                        labels: {
                            style: {
                                textTransform: "capitalize",
                                fontSize: "11px",
                                color: "#919191",
                            },
                        },
                        tickInterval: this.state.availableGran[2].disabled
                            ? tickIntervals.daily * 2
                            : tickIntervals.monthly,
                        minPadding: 0,
                        maxPadding: 0,
                    },
                },
            ],
        );
        return config;
    };

    public getPOPyAxisFormatter = () => {
        const { tab } = this.state;
        const filter = tabsMeta[tab].filter;
        const yAxisFilter = tabsMeta[tab].yAxisFilter;
        const yAxisFormatter = ({ value }) =>
            yAxisFilter ? yAxisFilter[0]()(value, filter[1]) : filter[0]()(value, filter[1]);

        return yAxisFormatter;
    };
}
